import { css } from "@emotion/css";
import { theme } from "assets/theme";
import React, { useState, useEffect, useRef } from "react";
import { Input } from "../input";
import { Text } from "../text";
import axios from "axios";

let debounceTimeout: NodeJS.Timeout;
type Props = {
  sourceUrl: string;
  placeholder?: string;
  value?: string;
  onSelect: (value: string) => void;
};
export const AutoCompleteInput: React.FC<Props> = ({
  sourceUrl,
  value,
  placeholder,
  onSelect,
}) => {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState<
    { id: string; value: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close suggestions if user clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event?.target as Node)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Clear any existing timer when the query changes
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // If the input is empty, clear suggestions
    if (!query.trim() || query.length < 3 || query === value) {
      setSuggestions([]);
      return;
    }

    // Debounce API calls by 300ms
    debounceTimeout = setTimeout(() => {
      setLoading(true);

      const fetchSuggestions = async () => {
        try {
          // Replace with your actual API endpoint
          //const response = await fetch(
          //  `${sourceUrl}${encodeURIComponent(query)}`,
          //);
          const response = await axios.get(
            `${sourceUrl}${encodeURIComponent(query)}`,
          );
          const data = response.data;
          if (data.status === "OK") {
            setSuggestions(
              data.predictions.map((prediction: any) => ({
                id: prediction.place_id,
                value: prediction.description,
              })),
            );
          }
        } catch (error) {
          setSuggestions([]);
          console.error("Error fetching suggestions:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchSuggestions();
    }, 300);

    // Cleanup on unmount or when query changes
    return () => clearTimeout(debounceTimeout);
  }, [query, value, sourceUrl]);

  // Handle click on a suggestion
  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    onSelect(suggestion);
    setTimeout(() => {
      setSuggestions([]);
    }, 1000);
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      <Input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        style={{ width: "100%", boxSizing: "border-box" }}
      />
      {loading && (
        <Text.Body2XS
          style={{ position: "absolute", top: "40px", left: "0px" }}
        >
          Loading...
        </Text.Body2XS>
      )}
      {suggestions.length > 0 && (
        <ul
          className={css`
            list-style: none;
            margin: 0;
            padding: 8px 0px;
            border: 1px solid ${theme.colors.border.primary};
            border-top: none;
            position: absolute;
            border-radius: 0 0 8px 8px;
            width: 100%;
            background: ${theme.colors.background.secondary};
            z-index: 1000;
          `}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion.value)}
              className={css`
                padding: 4px 8px;
                cursor: pointer;
                &:hover {
                  background: ${theme.colors.text["highlight/1"]};
                }
              `}
            >
              <Text.Body2XS color="seconday">{suggestion.value}</Text.Body2XS>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
